import React from "react";
import { RotateCcw } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  DEFAULT_LEAGUE_SCORING,
  LeagueScoringConfig,
} from "../lib/scoring";

interface LeagueScoringFieldsProps {
  value: LeagueScoringConfig;
  onChange: (field: keyof LeagueScoringConfig, value: number) => void;
  isCommander?: boolean;
}

type ScoringField = {
  key: keyof LeagueScoringConfig;
  label: string;
  min: number;
};

const sharedFields: ScoringField[] = [
  { key: "participationPoints", label: "Participation", min: 0 },
  { key: "premiumMultiplier", label: "Premium multiplier", min: 1 },
];

const matchFields: ScoringField[] = [
  { key: "winPoints", label: "Match win", min: 0 },
  { key: "drawPoints", label: "Match draw", min: 0 },
  { key: "lossPoints", label: "Match loss", min: 0 },
  { key: "top8FirstPoints", label: "1st place", min: 0 },
  { key: "top8SecondPoints", label: "2nd place", min: 0 },
  { key: "top8ThirdFourthPoints", label: "3rd–4th place", min: 0 },
  { key: "top8FifthEighthPoints", label: "5th–8th place", min: 0 },
];

const commanderFields: ScoringField[] = [
  { key: "commanderFirstPoints", label: "Pod 1st place", min: 0 },
  { key: "commanderSecondPoints", label: "Pod 2nd place", min: 0 },
  { key: "commanderThirdPoints", label: "Pod 3rd place", min: 0 },
  { key: "commanderFourthPoints", label: "Pod 4th place", min: 0 },
];

const LeagueScoringFields: React.FC<LeagueScoringFieldsProps> = ({
  value,
  onChange,
  isCommander = false,
}) => {
  const fields = [
    ...sharedFields,
    ...(isCommander ? commanderFields : matchFields),
  ];

  return (
  <fieldset className="space-y-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <legend className="font-semibold">Point allocation</legend>
        <p className="mt-1 text-sm text-muted-foreground">
          {isCommander
            ? "Pod placement points apply each round. Participation is added once per event."
            : "These values apply only to this league and recalculate its standings."}
        </p>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() =>
          Object.entries(DEFAULT_LEAGUE_SCORING).forEach(([field, points]) =>
            onChange(field as keyof LeagueScoringConfig, points)
          )
        }
      >
        <RotateCcw className="mr-2 h-4 w-4" />
        Reset defaults
      </Button>
    </div>

    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {fields.map(({ key, label, min }) => (
        <div key={key} className="space-y-2">
          <Label htmlFor={key}>{label}</Label>
          <Input
            id={key}
            type="number"
            min={min}
            step="1"
            value={value[key]}
            onChange={(event) =>
              onChange(key, Math.max(min, Number(event.target.value)))
            }
            required
          />
        </div>
      ))}
    </div>
  </fieldset>
  );
};

export default LeagueScoringFields;
